import { useCallback, useEffect } from "react";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import type {
  FluxZapBatchTaskView,
  FluxZapCommunicationRecord,
} from "../types";
import {
  CLINICAL_CRM_COMMUNICATIONS_EMPTY_DATA,
  type ClinicalCrmCommunicationsData,
  clinicalCrmCommunicationsService,
} from "../services/clinicalCrmCommunicationsService";

export interface UseClinicalCrmCommunicationsDataResult {
  communications: FluxZapCommunicationRecord[];
  batchTasks: FluxZapBatchTaskView[];
  isLoading: boolean;
  loadError: unknown;
  refetch: () => void;
}

/**
 * Carrega Basic (comunicações) e Task (disparos em lote) via FHIR para a seção de gestão.
 */
export function useClinicalCrmCommunicationsData(
  organizationId: string | undefined,
  refreshSignal: number = 0,
): UseClinicalCrmCommunicationsDataResult {
  const fetchData = useCallback((orgId: string) => {
    return clinicalCrmCommunicationsService.fetchClinicalCrmCommunicationsData(
      orgId,
    );
  }, []);

  const { run, loading, data, error, reset } = useAsyncTask<
    [string],
    ClinicalCrmCommunicationsData
  >({
    task: fetchData,
    initialData: CLINICAL_CRM_COMMUNICATIONS_EMPTY_DATA,
  });

  useEffect(() => {
    const trimmed = organizationId?.trim();
    if (!trimmed) {
      reset();
      return;
    }
    void run(trimmed);
  }, [organizationId, refreshSignal, run, reset]);

  const refetch = useCallback(() => {
    const trimmed = organizationId?.trim();
    if (trimmed) {
      void run(trimmed);
    }
  }, [organizationId, run]);

  return {
    communications: data?.communications ?? [],
    batchTasks: data?.batchTasks ?? [],
    isLoading: loading,
    loadError: error,
    refetch,
  };
}
